import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  Flex,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  HStack,
  VStack,
  Divider,
  Badge,
  Center,
  Spinner,
  Icon,
  Button,
  IconButton,
  Textarea,
} from "@chakra-ui/react";
import {
  FaUser,
  FaTruck,
  FaDollarSign,
  FaUserTie,
  FaRoad,
  FaMoneyBillWave,
  FaTag,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { MdDesignServices, MdLocalShipping } from "react-icons/md";
import { FcDataConfiguration } from "react-icons/fc";
import VeiculoAutocomplete from "./veiculo-autocomplete";
import {
  fetchFrotaAtendimento,
  resolveMotoristaDoVeiculo,
  getVeiculoPorId,
} from "../apis/frota-supabase-api";
import {
  formatCondPgtoLabel,
  formatTransportadoraLabel,
  formatVendedorLabel,
  TRANSPORTADORAS_API_ENABLED,
} from "../utils/atendimentoSessionCache";

export const MAX_LACRES = 14;

const CORES_LACRE = [
  { value: "AZUL", label: "Azul" },
  { value: "VERMELHO", label: "Vermelho" },
  { value: "AMARELO", label: "Amarelo" },
  { value: "VERDE", label: "Verde" },
  { value: "BRANCO", label: "Branco" },
];

const TIPOS_SAIDA_TES = [
  { value: "", label: "Padrão (sem TES específica)" },
  { value: "TES01", label: "TES 01 – Remessa" },
  { value: "TES02", label: "TES 02 – Retorno" },
  { value: "TES03", label: "TES 03 – Bonificação" },
];

const fieldLabelProps = {
  color: "#007abe",
  fontWeight: "semibold",
  fontSize: "xs",
  mb: 1,
  textTransform: "uppercase",
  letterSpacing: "wider",
};

const inputProps = {
  focusBorderColor: "#007abe",
  borderColor: "gray.300",
  bg: "gray.50",
  fontSize: "sm",
  borderRadius: "lg",
  _hover: { borderColor: "#007abe" },
};

const LancamentoPedidoPanel = ({
  embedded = false,
  rowItem,
  valoresSelecionados = [],
  parsedCondPagamentos,
  parsedVendedores,
  parsedTransportadoras,
  condPagamentoSelecionado,
  onCondPagamentoChange,
  showAdditionalInputs,
  veiculoSelecionado,
  onVeiculoChange,
  motoristaNome,
  motoristaId = "",
  onMotoristaNomeChange,
  onMotoristaChange,
  gerarBoleto,
  onGerarBoletoChange,
  lacres = [],
  onLacreChange,
  onAddLacre,
  onRemoveLacre,
  tipoSaida,
  onTipoSaidaChange,
  onVolumeBocasChange,
  parcelasVencimento,
  onParcelasVencimentoChange,
  operacaoSelecionada,
  onOperacaoChange,
  tipoFreteSelecionado,
  onTipoFreteChange,
  transportadoraSelecionado,
  onTransportadoraChange,
  obsAtendimentoSelecionada,
  onObsAtendimentoChange,
  selectedVendedor,
  onVendedorChange,
  tipoOperacaoOptions,
  tipoFreteOptions,
}) => {
  const [veiculos, setVeiculos] = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [vinculos, setVinculos] = useState([]);
  const [loadingFrota, setLoadingFrota] = useState(true);
  const [frotaErro, setFrotaErro] = useState("");
  const [motoristaAutomatico, setMotoristaAutomatico] = useState(false);
  const [showTipoSaida, setShowTipoSaida] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadingFrota(true);
      setFrotaErro("");
      try {
        const { veiculos: vList, motoristas: mList, vinculos: vinList } =
          await fetchFrotaAtendimento();
        setVeiculos(vList);
        setMotoristas(mList);
        setVinculos(vinList);
      } catch (e) {
        console.error("Erro ao carregar frota:", e);
        setFrotaErro(e.message || "Erro ao carregar veículos e motoristas");
        setVeiculos([]);
        setMotoristas([]);
        setVinculos([]);
      } finally {
        setLoadingFrota(false);
      }
    };
    load();
  }, []);

  const veiculoAtual = useMemo(
    () => getVeiculoPorId(veiculos, veiculoSelecionado),
    [veiculos, veiculoSelecionado],
  );

  const capacidadeTotalVeiculo = useMemo(() => {
    if (!veiculoAtual) return 0;
    if (veiculoAtual.capacidade_total != null) {
      return Number(veiculoAtual.capacidade_total) || 0;
    }
    const caps = Array.isArray(veiculoAtual.litros_por_boca)
      ? veiculoAtual.litros_por_boca
      : [];
    return caps.reduce((s, c) => s + (Number(c) || 0), 0);
  }, [veiculoAtual]);

  useEffect(() => {
    if (!veiculoSelecionado) {
      onMotoristaNomeChange("");
      onMotoristaChange?.(null);
      onVolumeBocasChange([]);
      setMotoristaAutomatico(false);
      return;
    }

    const resolved = resolveMotoristaDoVeiculo(veiculoSelecionado, {
      vinculos,
      motoristas,
    });
    if (resolved) {
      onMotoristaNomeChange(resolved.nome);
      onMotoristaChange?.(resolved);
      setMotoristaAutomatico(true);
    } else {
      onMotoristaNomeChange("");
      onMotoristaChange?.(null);
      setMotoristaAutomatico(false);
    }

    const caps = Array.isArray(veiculoAtual?.litros_por_boca)
      ? veiculoAtual.litros_por_boca
      : [];
    const n = Math.max(
      veiculoAtual?.bocas || 0,
      caps.length,
    );
    onVolumeBocasChange(
      Array.from({ length: n }, (_, i) => ({
        boca: i + 1,
        capacidade: Number(caps[i]) || 0,
        volume: 0,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veiculoSelecionado, veiculoAtual, vinculos, motoristas]);

  const simularVencimentos = () => {
    const cond = parsedCondPagamentos.find(
      (c) => String(c.codigo) === String(condPagamentoSelecionado),
    );
    const nParcelas = cond?.parcelas || 4;
    const hoje = new Date();
    const novas = Array.from({ length: nParcelas }, (_, i) => {
      const d = new Date(hoje);
      d.setDate(d.getDate() + (i + 1) * 30);
      return {
        parcela: i + 1,
        valor: "",
        data: d.toISOString().slice(0, 10),
      };
    });
    onParcelasVencimentoChange(novas);
  };

  if (!rowItem) return null;

  const formulario = (
    <Box p={embedded ? 0 : { base: 4, md: 5 }}>
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }}
          gap={4}
          mb={6}
        >
          <GridItem colSpan={{ base: 1, md: 2, xl: 3 }}>
            <Box
              p={4}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.200"
              bg="gray.50"
            >
              <Text
                fontSize="sm"
                fontWeight="bold"
                color="#007abe"
                mb={3}
                display="flex"
                alignItems="center"
              >
                <Icon as={FaUser} mr={2} color="#c0a316" />
                Cliente
              </Text>
              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }}
                gap={3}
              >
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Código
                  </Text>
                  <Text fontWeight="semibold">{rowItem.codigo}</Text>
                </Box>
                <Box gridColumn={{ sm: "span 2" }}>
                  <Text fontSize="xs" color="gray.500">
                    Razão social / Nome
                  </Text>
                  <Text fontWeight="semibold" noOfLines={2}>
                    {rowItem.nome || rowItem.razao_social || rowItem.contato}
                  </Text>
                </Box>
              </Grid>
            </Box>
          </GridItem>

          <GridItem>
            <FormControl isRequired>
              <FormLabel {...fieldLabelProps}>
                <Icon as={FaUserTie} mr={1} /> Comprador
              </FormLabel>
              <Input
                {...inputProps}
                isReadOnly
                bg="gray.100"
                value={
                  rowItem.nome ||
                  rowItem.razao_social ||
                  rowItem.contato ||
                  "—"
                }
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Cliente do atendimento · Cód. {rowItem.codigo}
              </Text>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl isRequired>
              <FormLabel {...fieldLabelProps}>
                <Icon as={FaTruck} mr={1} /> Veículo
              </FormLabel>
              {loadingFrota ? (
                <Center py={2}>
                  <Spinner size="sm" color="#007abe" />
                </Center>
              ) : frotaErro ? (
                <Text fontSize="xs" color="red.500">
                  {frotaErro}
                </Text>
              ) : (
                <VeiculoAutocomplete
                  veiculos={veiculos}
                  value={veiculoSelecionado}
                  onChange={onVeiculoChange}
                  placeholder="Digite a placa ou clique para listar"
                  inputProps={inputProps}
                />
              )}
              {veiculoAtual && (
                <HStack mt={2} spacing={2} flexWrap="wrap">
                  <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                    Volume total:{" "}
                    {capacidadeTotalVeiculo.toLocaleString("pt-BR")} L
                  </Badge>
                  {veiculoAtual.bocas ? (
                    <Badge colorScheme="gray" variant="subtle" fontSize="xs">
                      {veiculoAtual.bocas} bocas
                    </Badge>
                  ) : null}
                </HStack>
              )}
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl isRequired={Boolean(veiculoSelecionado)}>
              <FormLabel {...fieldLabelProps}>
                {motoristaAutomatico
                  ? "Motorista (automático)"
                  : "Motorista"}
              </FormLabel>
              {motoristaAutomatico || !veiculoSelecionado ? (
                <Input
                  {...inputProps}
                  value={
                    !veiculoSelecionado
                      ? ""
                      : motoristaNome
                  }
                  isReadOnly
                  placeholder="Selecione um veículo"
                  bg="gray.100"
                />
              ) : (
                <Select
                  {...inputProps}
                  placeholder="Selecione o motorista"
                  value={motoristaId || ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    const m = motoristas.find((x) => String(x.id) === String(id));
                    if (m) {
                      onMotoristaNomeChange(m.nome || "");
                      onMotoristaChange?.({
                        id: m.id,
                        nome: m.nome || "",
                        cpf: m.cpf || "",
                      });
                    } else {
                      onMotoristaNomeChange("");
                      onMotoristaChange?.(null);
                    }
                  }}
                >
                  {motoristas.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.nome}
                      {m.cpf ? ` — ${m.cpf}` : ""}
                    </option>
                  ))}
                </Select>
              )}
              {veiculoSelecionado && !motoristaAutomatico && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Nenhum motorista vinculado a este veículo. Selecione na lista
                  (cadastro de frota).
                </Text>
              )}
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl
              isRequired
              isInvalid={
                Array.isArray(parsedCondPagamentos) &&
                parsedCondPagamentos.length === 0
              }
            >
              <FormLabel {...fieldLabelProps}>
                <Icon as={FaDollarSign} mr={1} /> Condição de pagamento
              </FormLabel>
              <Select
                {...inputProps}
                placeholder={
                  parsedCondPagamentos?.length
                    ? "Selecione a condição"
                    : "Nenhuma condição disponível"
                }
                value={condPagamentoSelecionado || ""}
                onChange={onCondPagamentoChange}
                isDisabled={!parsedCondPagamentos?.length}
              >
                {(parsedCondPagamentos || []).map((opt) => (
                  <option
                    key={String(opt.codigo)}
                    value={String(opt.codigo)}
                  >
                    {formatCondPgtoLabel(opt)}
                  </option>
                ))}
              </Select>
              {(!parsedCondPagamentos ||
                parsedCondPagamentos.length === 0) && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  Condições de pagamento não carregadas. Recarregue o
                  atendimento.
                </Text>
              )}
            </FormControl>
          </GridItem>

          <GridItem display="flex" alignItems="flex-end">
            <Checkbox
              colorScheme="blue"
              isChecked={gerarBoleto}
              onChange={(e) => onGerarBoletoChange(e.target.checked)}
              borderColor="#007abe"
            >
              <Text fontSize="sm" fontWeight="medium">
                Gerar boleto
              </Text>
            </Checkbox>
          </GridItem>

          <GridItem colSpan={{ base: 1, md: 2, xl: 3 }}>
            <FormControl>
              <Flex align="center" justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                <FormLabel {...fieldLabelProps} mb={0}>
                  <Icon as={FaTag} mr={1} /> Lacres
                </FormLabel>
                <Text fontSize="xs" color="gray.500">
                  {lacres.length} de {MAX_LACRES}
                </Text>
              </Flex>

              <VStack align="stretch" spacing={2}>
                {lacres.map((lacre, index) => (
                  <Flex
                    key={index}
                    gap={2}
                    align={{ base: "stretch", sm: "center" }}
                    direction={{ base: "column", sm: "row" }}
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.500"
                      minW="1.25rem"
                      display={{ base: "none", sm: "block" }}
                    >
                      {index + 1}.
                    </Text>
                    <Select
                      {...inputProps}
                      size="sm"
                      flex={{ sm: "0 0 140px" }}
                      placeholder="Cor"
                      value={lacre.cor}
                      onChange={(e) =>
                        onLacreChange(index, "cor", e.target.value)
                      }
                    >
                      {CORES_LACRE.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </Select>
                    <Input
                      {...inputProps}
                      size="sm"
                      flex={1}
                      value={lacre.numero}
                      onChange={(e) =>
                        onLacreChange(index, "numero", e.target.value)
                      }
                      placeholder="Número do lacre"
                    />
                    <IconButton
                      aria-label="Remover lacre"
                      icon={<FaTrash />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      flexShrink={0}
                      isDisabled={lacres.length <= 1}
                      onClick={() => onRemoveLacre(index)}
                    />
                  </Flex>
                ))}
              </VStack>

              {lacres.length < MAX_LACRES && (
                <Button
                  mt={2}
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  borderColor="#007abe"
                  color="#007abe"
                  leftIcon={<FaPlus />}
                  onClick={onAddLacre}
                >
                  Adicionar lacre
                </Button>
              )}
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel {...fieldLabelProps}>Tipo de saída (TES)</FormLabel>
              <Checkbox
                mb={2}
                size="sm"
                colorScheme="blue"
                isChecked={showTipoSaida}
                onChange={(e) => {
                  setShowTipoSaida(e.target.checked);
                  if (!e.target.checked) onTipoSaidaChange("");
                }}
              >
                TES específica
              </Checkbox>
              {showTipoSaida && (
                <Select
                  {...inputProps}
                  value={tipoSaida}
                  onChange={(e) => onTipoSaidaChange(e.target.value)}
                >
                  {TIPOS_SAIDA_TES.filter((t) => t.value).map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>
          </GridItem>
        </Grid>

        {showAdditionalInputs && (
          <Box
            mb={6}
            p={4}
            borderRadius="lg"
            border="1px dashed"
            borderColor="#007abe"
            bg="gray.50"
          >
            <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
              <Text fontWeight="bold" color="#007abe" fontSize="sm">
                Simulação de vencimentos (condição especial)
              </Text>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                onClick={simularVencimentos}
              >
                Simular parcelas (+30 dias)
              </Button>
            </Flex>
            <VStack align="stretch" spacing={2}>
              {parcelasVencimento.map((p, idx) => (
                <HStack key={p.parcela} spacing={3} flexWrap="wrap">
                  <Text fontSize="sm" minW="90px" fontWeight="medium">
                    {p.parcela}ª parcela
                  </Text>
                  <Input
                    type="number"
                    placeholder="Valor R$"
                    size="sm"
                    w="140px"
                    {...inputProps}
                    value={p.valor}
                    onChange={(e) => {
                      const next = [...parcelasVencimento];
                      next[idx] = { ...p, valor: e.target.value };
                      onParcelasVencimentoChange(next);
                    }}
                  />
                  <Input
                    type="date"
                    size="sm"
                    w="160px"
                    {...inputProps}
                    value={p.data}
                    onChange={(e) => {
                      const next = [...parcelasVencimento];
                      next[idx] = { ...p, data: e.target.value };
                      onParcelasVencimentoChange(next);
                    }}
                  />
                </HStack>
              ))}
            </VStack>
          </Box>
        )}

        <Divider mb={6} borderColor="gray.200" />

        <Text
          fontSize="md"
          fontWeight="bold"
          color="#007abe"
          mb={4}
          display="flex"
          alignItems="center"
        >
          Complementos do atendimento
        </Text>
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }}
          gap={4}
          mb={6}
        >
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...fieldLabelProps}>
                <Icon as={FaMoneyBillWave} mr={1} /> Operação
              </FormLabel>
              <Select
                {...inputProps}
                value={operacaoSelecionada}
                onChange={onOperacaoChange}
              >
                {tipoOperacaoOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel {...fieldLabelProps}>
                <Icon as={FaRoad} mr={1} /> Tipo frete
              </FormLabel>
              <Select
                {...inputProps}
                value={tipoFreteSelecionado}
                onChange={onTipoFreteChange}
              >
                {tipoFreteOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired={TRANSPORTADORAS_API_ENABLED}>
              <FormLabel {...fieldLabelProps}>
                <Icon as={MdLocalShipping} mr={1} /> Transportadora
                {!TRANSPORTADORAS_API_ENABLED && (
                  <Text as="span" fontSize="xs" color="gray.500" ml={1}>
                    (opcional)
                  </Text>
                )}
              </FormLabel>
              <Select
                {...inputProps}
                placeholder={
                  !TRANSPORTADORAS_API_ENABLED
                    ? "API indisponível — seguir sem transportadora"
                    : parsedTransportadoras?.length
                      ? "Selecione a transportadora"
                      : "Nenhuma transportadora disponível"
                }
                value={transportadoraSelecionado || ""}
                onChange={onTransportadoraChange}
                isDisabled={
                  !TRANSPORTADORAS_API_ENABLED ||
                  !parsedTransportadoras?.length
                }
              >
                {(parsedTransportadoras || []).map((t) => (
                  <option key={String(t.codigo)} value={String(t.codigo)}>
                    {formatTransportadoraLabel(t)}
                  </option>
                ))}
              </Select>
              {!TRANSPORTADORAS_API_ENABLED ? (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Carregamento de transportadoras desabilitado temporariamente.
                </Text>
              ) : (
                (!parsedTransportadoras ||
                  parsedTransportadoras.length === 0) && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    Transportadoras não carregadas. Recarregue o atendimento.
                  </Text>
                )
              )}
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl
              isRequired
              isInvalid={
                Array.isArray(parsedVendedores) &&
                parsedVendedores.length === 0
              }
            >
              <FormLabel {...fieldLabelProps}>Vendedor</FormLabel>
              <Select
                {...inputProps}
                placeholder={
                  parsedVendedores?.length
                    ? "Selecione o vendedor"
                    : "Nenhum vendedor disponível"
                }
                value={selectedVendedor || ""}
                onChange={onVendedorChange}
                isDisabled={!parsedVendedores?.length}
              >
                {(parsedVendedores || []).map((v) => (
                  <option key={String(v.codigo)} value={String(v.codigo)}>
                    {formatVendedorLabel(v)}
                  </option>
                ))}
              </Select>
              {(!parsedVendedores || parsedVendedores.length === 0) && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  Vendedores não carregados. Recarregue o atendimento.
                </Text>
              )}
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, md: 2, xl: 4 }}>
            <FormControl isRequired>
              <FormLabel {...fieldLabelProps}>
                <Icon as={MdDesignServices} mr={1} /> Observação do atendimento
              </FormLabel>
              <Textarea
                {...inputProps}
                rows={2}
                value={obsAtendimentoSelecionada}
                onChange={onObsAtendimentoChange}
                placeholder="Observações durante o atendimento"
              />
            </FormControl>
          </GridItem>
        </Grid>
    </Box>
  );

  if (embedded) return formulario;

  return (
    <Box
      bg="white"
      borderRadius="10px"
      shadow="lg"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
    >
      <Box
        bgGradient="linear(to-r, #007abe, #005a8a)"
        color="white"
        px={{ base: 4, md: 5 }}
        py={3}
      >
        <Flex align="center" justify="space-between" flexWrap="wrap" gap={2}>
          <HStack spacing={3}>
            <Flex
              w="40px"
              h="40px"
              borderRadius="lg"
              bg="whiteAlpha.200"
              align="center"
              justify="center"
            >
              <Icon as={FcDataConfiguration} boxSize={5} />
            </Flex>
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                Lançamento de Pedido
              </Text>
              <Text fontSize="xs" opacity={0.9}>
                Portal Cenze
              </Text>
            </Box>
          </HStack>
          <Badge
            bg="whiteAlpha.300"
            color="white"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
          >
            {valoresSelecionados.length} item(ns) no pedido
          </Badge>
        </Flex>
      </Box>
      {formulario}
    </Box>
  );
};

export default LancamentoPedidoPanel;
